<template>
  <div class="inventory">
    <h1 class="mb-4">Inventory Management</h1>
    <div class="table-tabs pb-0">
      <b-nav tabs>
        <b-nav-item
          v-for="tab in tabs"
          :key="tab.name"
          :to="{ name: tab.name, params: { customerId } }"
          :active="isActive(tab.name)"
          router
        >
          {{ tab.label }}
        </b-nav-item>
      </b-nav>
    </div>

    <div class="bg-white table-shadow p-3 mb-3">
      <strong>Customer</strong>
      <span class="ms-2">{{ selectedCompanyName }}</span>
      <div
        v-if="customersStore.customer"
        class="mt-2"
        :class="customersStore.customer.isInventoryManagementAvailable ? 'text-success' : 'text-danger'"
      >
        <span v-if="customersStore.customer.isInventoryManagementAvailable">
          Company has inventory management enabled, in Scan&amp;Go upon scan on hand value will be decreased for products in the shipment.
        </span>
        <span v-else>
          Company has inventory management disabled, you can still manage products and inbound deliveries here but Scan&amp;Go will not modify the on hand value of the products. When you are ready enable the inventory in company settings.
        </span>
      </div>
    </div>

    <div class="pb-5">
      <router-view v-slot="{ Component }">
        <component :is="Component" :company-id="customerId" />
      </router-view>
    </div>
  </div>
</template>

<script lang="ts">
import { BNav, BNavItem } from 'bootstrap-vue-next'
import { defineComponent, computed, watch } from 'vue'
import { onBeforeRouteLeave, useRoute } from 'vue-router'
import { useCustomersStore } from '@/store/customers'
import { useInventoryStore } from '@/store/inventory'
import {
  STAFF_INVENTORY_INBOUND_RECEIPTS,
  STAFF_INVENTORY_PRODUCTS,
  STAFF_INVENTORY_TRANSACTIONS,
} from '@/types/router'

export default defineComponent({
  name: 'InventoryCustomerLayout',
  components: { BNav, BNavItem },
  setup() {
    const route = useRoute()
    const customersStore = useCustomersStore()
    const inventoryStore = useInventoryStore()

    const customerId = computed(() => route.params.customerId as string)
    const selectedCompanyName = computed(() => customersStore.customer?.name || '-')

    const tabs = [
      { name: STAFF_INVENTORY_PRODUCTS, label: 'Products' },
      { name: STAFF_INVENTORY_INBOUND_RECEIPTS, label: 'Inbound Receipts' },
      { name: STAFF_INVENTORY_TRANSACTIONS, label: 'Transactions' },
    ]

    const isActive = (name: string) => route.name === name

    watch(
      () => customerId.value,
      (value, prevValue) => {
        if (value !== prevValue) {
          inventoryStore.resetState()
        }
        if (customersStore.customer?.id !== value) {
          customersStore.fetch(value)
        }
      },
      { immediate: true },
    )

    onBeforeRouteLeave(() => {
      inventoryStore.resetState()
      return undefined
    })

    return { customerId, selectedCompanyName, tabs, isActive, customersStore }
  },
})
</script>

<style scoped>
.inventory :deep(.table-tabs .nav-tabs .nav-link.active) {
  box-shadow: none;
}
</style>
